const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

function isElementInInitialViewport(element) {
  const rect = element.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0;
}

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    const elementTarget = element.target;

    if (element.isIntersecting) {
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      }

      if (elementTarget.hasAttribute('data-cascade')) {
        elementTarget.style.setProperty('--animation-order', index);
      }

      observer.unobserve(elementTarget);
    } else {
      elementTarget.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      elementTarget.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });

  /*
   * Complete the initial geometry-read phase before changing classes.
   * The animation appearance and timing remain unchanged.
   */
  const initialVisibility = animationTriggerElements.map((element) => isElementInInitialViewport(element));

  animationTriggerElements.forEach((element, index) => {
    if (initialVisibility[index]) {
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);

      if (element.hasAttribute('data-cascade')) {
        element.style.setProperty('--animation-order', index);
      }
    }

    observer.observe(element);
  });
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;
  const visibleElements = new Set();
  let animationFrame = 0;

  const requestZoomUpdate = () => {
    if (animationFrame || visibleElements.size === 0) return;

    animationFrame = window.requestAnimationFrame(() => {
      animationFrame = 0;
      const viewportHeight = window.innerHeight;
      const measurements = [];

      /*
       * Read all visible geometry first. Apply the existing zoom variables
       * only after every read is complete to prevent layout thrashing.
       */
      visibleElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        measurements.push({
          element,
          top: rect.top,
          height: rect.height,
        });
      });

      measurements.forEach(({ element, top, height }) => {
        let percentage = 0;

        if (top > viewportHeight) {
          percentage = 0;
        } else if (top + height < 0) {
          percentage = 100;
        } else {
          const distance = viewportHeight - top;
          percentage = Math.round(distance / ((viewportHeight + height) / 100));
        }

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentage);
      });
    });
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        visibleElements.add(entry.target);
      } else {
        visibleElements.delete(entry.target);
      }
    });

    requestZoomUpdate();
  });

  animationTriggerElements.forEach((element) => observer.observe(element));

  window.addEventListener('scroll', requestZoomUpdate, { passive: true });
  window.addEventListener('resize', requestZoomUpdate, { passive: true });
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
